/**
 * Pure helpers behind verified WhatsApp links and group membership on the CSP
 * (Nikhil 2026-09-13). A client user can now link their own WhatsApp number
 * from the portal ("Verified"), next to the rep-confirmed mapping that
 * already existed ("Mapped by rep"); group channels get explicit portal-user
 * members. Exercised by member_links.test.js without a backend.
 *
 * Contract (cs-analysis, built in parallel — every consumer is defensive):
 *   GET    /comms/clients/:storeId/members
 *            rows += verifiedVia:'self'|'rep'|null, mappedUserId, mappedAt
 *            (+ orgUsers[{id,name,email}] and reps[{userId,name}] as before)
 *   DELETE /comms/clients/:storeId/members/:id/link        unlink → guest again
 *   GET    /comms/channels/:id/members
 *            += portal users {kind:'CLIENT_USER', clientUserId, name, verifiedVia}
 *   POST   /comms/channels/:id/members { clientUserId }    add a portal user
 *   DELETE /comms/channels/:id/members/:clientUserId       remove one
 * Before the contract ships, the org list lives at …/wa-members and unlinking
 * is DELETE …/wa-members/:id/map; both fall back on a 404 so the screen keeps
 * working against today's backend.
 */

export const org_members_path = (store_id) => `/api/v1/comms/clients/${encodeURIComponent(String(store_id))}/members`;
export const legacy_org_members_path = (store_id) => `/api/v1/comms/clients/${encodeURIComponent(String(store_id))}/wa-members`;
export const unlink_path = (store_id, member_id) => `${org_members_path(store_id)}/${encodeURIComponent(String(member_id))}/link`;
export const legacy_unlink_path = (store_id, member_id) => `${legacy_org_members_path(store_id)}/${encodeURIComponent(String(member_id))}/map`;
export const channel_members_path = (channel_id) => `/api/v1/comms/channels/${encodeURIComponent(String(channel_id))}/members`;
export const channel_member_path = (channel_id, client_user_id) => `${channel_members_path(channel_id)}/${encodeURIComponent(String(client_user_id))}`;

const is_404 = (e) => !!e && Number(e.status) === 404;

/** Org member list on the contract route, falling back to the pre-contract
 *  route on 404. `fetcher` is cs_analysis_fetch (injected for tests). */
export const fetch_org_members = async (fetcher, store_id) => {
    try {
        return await fetcher({ path: org_members_path(store_id) });
    } catch (e) {
        if (!is_404(e)) throw e;
        return fetcher({ path: legacy_org_members_path(store_id) });
    }
};

/** Unlink a member (back to guest) — contract route, legacy unmap on 404. */
export const unlink_member = async (fetcher, store_id, member_id) => {
    try {
        return await fetcher({ path: unlink_path(store_id, member_id), method: 'DELETE' });
    } catch (e) {
        if (!is_404(e)) throw e;
        return fetcher({ path: legacy_unlink_path(store_id, member_id), method: 'DELETE' });
    }
};

// ── link chips ──────────────────────────────────────────────────────────────

/** 'self' | 'rep' | null — how a member's WhatsApp number was tied to a portal
 *  user. Infurnia reps are never client links. Older rows carry no verifiedVia:
 *  a mapped USER there was rep-confirmed by construction. */
export const link_kind = (m) => {
    if (!m || m.kind === 'REP') return null;
    if (m.verifiedVia === 'self' || m.verifiedVia === 'rep') return m.verifiedVia;
    if (m.kind === 'USER' && m.mappedUserId) return 'rep';
    return null;
};

export const LINK_CHIPS = {
    self: { label: 'Verified', title: 'The user linked this WhatsApp number themselves from the portal', fg: '#2e7d4f', bg: '#eefaf2', bd: '#cfeedd' },
    rep: { label: 'Mapped by rep', title: 'A CS rep mapped this number to the user', fg: '#2f6fed', bg: '#eef2fb', bd: '#c9d6f2' },
};

/** Chip descriptor {kind,label,title,fg,bg,bd} for a member row, or null for guests / Infurnia reps. */
export const member_link_chip = (m) => {
    const kind = link_kind(m);
    return kind ? { kind, ...LINK_CHIPS[kind] } : null;
};

/** A linked member can be unlinked (drops back to guest); guests and reps cannot. */
export const can_unlink = (m) => !!link_kind(m);

/** WhatsApp contact id for display: strip the @c.us suffix (masked ids pass through). */
export const mask_contact = (wa_contact_id) => String(wa_contact_id || '').replace(/@.*$/, '');

// ── group membership ────────────────────────────────────────────────────────

export const GROUP_MEMBER_SECTIONS = [
    { key: 'portal', label: 'Portal users with access', kinds: ['CLIENT_USER'] },
    { key: 'whatsapp', label: 'WhatsApp members', kinds: ['USER', 'GUEST'] },
    { key: 'team', label: 'Infurnia team', kinds: ['REP', 'STAFF'] },
];

export const GROUP_ACCESS_NOTE = 'general is visible to every user of the org; other groups only to members.';

export const GENERAL_GROUP_KEY = 'general';
export const is_general_group = (c) => !!c && String(c.groupKey || '').toLowerCase() === GENERAL_GROUP_KEY;

/** {portal, whatsapp, team} — channel member rows by section; unknown kinds
 *  stay on the WhatsApp side (today's behaviour), null rows are dropped. */
export const partition_group_members = (members) => {
    const out = { portal: [], whatsapp: [], team: [] };
    (Array.isArray(members) ? members : []).forEach((m) => {
        if (!m) return;
        const sec = GROUP_MEMBER_SECTIONS.find((s) => s.kinds.includes(m.kind));
        out[sec ? sec.key : 'whatsapp'].push(m);
    });
    return out;
};

/** Org users not yet in the group, matching the picker query on name / email. */
export const addable_org_users = (org_users, members, q) => {
    const in_group = new Set(partition_group_members(members).portal.map((m) => String(m.clientUserId)));
    const needle = String(q || '').trim().toLowerCase();
    return (Array.isArray(org_users) ? org_users : [])
        .filter((u) => u && u.id != null && u.id !== '' && !in_group.has(String(u.id)))
        .filter((u) => !needle || [u.name, u.email].some((f) => String(f || '').toLowerCase().includes(needle)));
};
